<template>
  <el-container direction="vertical" style="margin: 5vh 5vw">
    <div class="header" style="margin-bottom: 25px">
      <h2>
        Manage Tasks
        <el-tooltip placement="right" effect="light">
          <span slot="content"
            >A task is a discrete activity you create to process Planning
            Analytics data from<br />a source to a target location. You can
            perform actions such as copy, move, and<br />zero out.</span
          >
          <i
            class="el-icon-info"
            style="position: relative; left: 2px; bottom: 12px; font-size: 1rem"
          />
        </el-tooltip>
      </h2>
    </div>
    <div>
      <el-input
        v-model="filter"
        placeholder="Type to search"
        style="width: 30%; margin-bottom: 12px"
      />
      <el-button
        class="button"
        @click="pushHistory"
        :disabled="envRights === 'Run'"
        style="
          display: flex;
          justify-content: center;
          padding: 18px;
          width: 125px;
          height: 40px;
          float: right;
          line-height: 15%;
        "
      >
        View All History</el-button
      >
    </div>
    <el-table
      :data="itemsToShow"
      :default-sort="{ prop: 'name', order: 'ascending' }"
      style="border: 2px solid #ebeef5; width: 100%"
      @row-dblclick="pushEdit"
      max-height="60vh"
    >
      <div slot="empty">
        A task is a discrete activity you create to process Planning Analytics
        data from a source to a target location. Click the
        <b>Add Task</b> button to get started with your first task.
      </div>
      <el-table-column width="200">
        <template slot="header">
          <el-button
            class="button"
            @click="pushAdd"
            :disabled="envRights === 'Run'"
            icon="el-icon-plus"
            >Add Task</el-button
          >
        </template>
        <template slot-scope="scope">
          <el-button-group>
            <el-tooltip placement="bottom" content="Run Task">
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-caret-right"
                @click="runTask(scope.row)"
            /></el-tooltip>
            <el-tooltip
              placement="bottom"
              :content="envRights === 'Run' ? 'View Task' : 'Edit Task'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-edit"
                @click="push(scope.row, 'tasksedit')"
            /></el-tooltip>
            <el-tooltip placement="bottom" content="View History">
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-reading"
                @click="push(scope.row, 'taskshistory')"
            /></el-tooltip>
            <el-tooltip
              placement="bottom"
              content="Delete Task"
              v-if="envRights !== 'Run'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-delete"
                @click="deleteTask(scope.row)"
            /></el-tooltip>
            <el-button
              size="medium"
              class="hidden"
              icon="el-icon-delete"
              v-else
            />
          </el-button-group>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="Name" sortable :min-width="25" />
      <el-table-column prop="type" label="Type" sortable :min-width="10" />
      <el-table-column
        prop="description"
        label="Description"
        sortable
        :min-width="25"
      />
      <el-table-column
        prop="updatedBy"
        label="Last Modified By"
        sortable
        :min-width="20"
      />
      <el-table-column
        prop="updatedAt"
        label="Last Modified At"
        sortable
        :sort-method="sortDate"
        :min-width="20"
      />
    </el-table>
  </el-container>
</template>

<script>
import { mapState } from "vuex";
import TaskService from "../services/TaskService";
import moment from "moment-timezone";
import Helper from "../utilities/Helper";

export default {
  computed: {
    ...mapState([
      "instanceId",
      "taskId",
      "userId",
      "envRights",
      "isUserLoggedIn",
      "environmentId",
    ]),
  },
  data() {
    return {
      items: [],
      itemsToShow: [],
      filter: null,
      timezone:
        process.env.VUE_APP_SERVER_TIMEZONE ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  watch: {
    filter(value) {
      let filter = value.toLowerCase();
      this.itemsToShow = this.items.filter((item) => {
        return JSON.stringify(item).toLowerCase().includes(filter);
      });
    },
  },
  methods: {
    handleError: Helper.handleError,
    pushEdit(row) {
      this.push(row, "tasksedit");
    },
    push(row, name) {
      this.$store.dispatch("setCurrentTaskName", row.name);
      this.$store.dispatch("setTaskId", row.id);
      this.$router.push({ name });
    },
    pushAdd() {
      this.$store.dispatch("setCurrentTaskName", null);
      this.$store.dispatch("setTaskId", null);
      this.$router.push({ name: "tasksadd" });
    },
    pushHistory() {
      this.$router.push({ name: "allhistory" });
    },
    /**
     * Runs the task
     * @function
     */
    async runTask(row) {
      if (confirm(`Are you sure you want to run the "${row.name}" task?`)) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await TaskService.run({
            taskId: row.id,
            userId: this.userId,
          });

          this.$message({
            type: "success",
            message: `The "${row.name}" task has been kicked off successfully. You'll receive an email when it finishes running. Check the task's history log for updates until then.`,
            duration: 5000,
            showClose: true,
          });
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      }
    },
    async deleteTask(row) {
      if (confirm(`Are you sure you want to delete the "${row.name}" task?`)) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await TaskService.delete({
            taskId: row.id,
          });
          this.$store.dispatch("setCurrentTaskName", null);
          this.$store.dispatch("setTaskId", null);
          this.taskCheckValid = false;
          this.$message({
            type: "success",
            message: `"${row.name}" task deleted successfully`,
            duration: 5000,
            showClose: true,
          });
          this.getTasks();
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      }
    },
    async getTasks() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const response = await TaskService.show({
          instanceId: this.instanceId,
        });
        const tasks = response.data.tasks;
        const users = response.data.users;
        this.items = [];
        let items = [];
        for (let i = 0; i < tasks.length; i++) {
          const createdBy = users.find(
            (user) => user.id === tasks[i].createdBy
          );
          const updatedBy = users.find(
            (user) => user.id === tasks[i].updatedBy
          );
          items[i] = {
            id: tasks[i].id,
            name: tasks[i].name,
            type: tasks[i].type,
            description: tasks[i].description,
            source: tasks[i].source,
            target: tasks[i].target,
            createdBy: createdBy.displayName,
            updatedBy: updatedBy.displayName,
            updatedAt: moment(tasks[i].updatedAt)
              .tz(this.timezone)
              .format("h:mm:ss A on LL"),
          };
          this.items.push(items[i]);
        }
        this.itemsToShow = this.items;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * The purpose: The purpose is to have a sorting algo for the custom sort.
     * @param {object} a is a object that has a date
     * @param {object} b is a object that has a date
     */
    sortDate(a, b) {
      return (
        this.toFloatFromDate(a.updatedAt) - this.toFloatFromDate(b.updatedAt)
      );
    },
    toFloatFromDate(num) {
      let date = new Date(num.split(" on ").join(" "));
      return date;
    },
  },
  mounted() {
    if (!this.isUserLoggedIn) {
      this.$router.push({ name: "login" });
    } else if (!this.instanceId) {
      this.$router.push({ name: "servers" });
    } else {
      this.getTasks();
      if (this.taskId) this.$store.dispatch("setTaskId", null);
      if (this.currentTaskName)
        this.$store.dispatch("setCurrentTaskName", null);
    }
  },
};
</script>

<style scoped>
.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
  padding: 10px 15px;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.table-button {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
.table-button:focus {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
}
.table-button:hover {
  background-color: #ecf5fe;
  color: #606266;
  border: none;
  outline: none;
}
.hidden:focus {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
}
.hidden:hover {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
}
.hidden {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
</style>
